import * as yup from 'yup'

export const toolsFormSchema = yup.object({
    toolName: yup
        .string()
        .required('Tool name is required')
        .min(2, 'Tool name must be at least 2 characters')
        .max(255, 'Tool name cannot exceed 255 characters'),

    toolType: yup
        .string()
        .oneOf(['LLM', 'Voice', 'Ai_image', 'Ai_idea', 'Ai_script'], 'Tool type must be either LLM, Voice, AI Image, AI Idea, or AI Script')
        .required('Tool type is required'),

    webview: yup
        .string()
        .oneOf(['NATIVE', 'WEBVIEW'], 'System type must be either NATIVE or WEBVIEW')
        .required('System type is required')
        .transform((value, originalValue) => {
            const v = (typeof originalValue === 'string' ? originalValue : value) as string
            return v?.toUpperCase?.() ?? v
        })
        .default('NATIVE'),

    webviewUrl: yup
        .string()
        .nullable()
        .when('webview', {
            is: 'WEBVIEW',
            then: (schema) =>
                schema
                    .required('Webview URL is required')
                    .url('Webview URL must be a valid URL'),
            otherwise: (schema) => schema.notRequired(),
        })
        .max(2048, 'Webview URL cannot exceed 2048 characters'),

    display: yup
        .string()
        .oneOf(['entity', 'global'], 'Display must be either entity or global')
        .required('Display is required'),

    description: yup
        .string()
        .nullable()
        .max(1000, 'Description cannot exceed 1000 characters'),

    toolPrompt: yup
        .string()
        .nullable()
        .max(5000, 'Tool prompt cannot exceed 5000 characters'),

    itemType: yup
        .string()
        .when('display', {
            is: 'entity',
            then: (schema) => schema.required('Item type is required for entity display'),
            otherwise: (schema) => schema.nullable(),
        }),

    items: yup
        .string()
        .when('display', {
            is: 'entity',
            then: (schema) => schema.required('Items is required for entity display'),
            otherwise: (schema) => schema.nullable(),
        }),

    itemName: yup
        .string()
        .nullable()
        .max(255, 'Item name cannot exceed 255 characters'),

    icon: yup
        .string()
        .required('Icon is required'),

    primaryColor: yup
        .string()
        .required('Primary color is required')
        .matches(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, {
            message: 'Primary color must be a valid hex color',
        }),

    secondaryColor: yup
        .string()
        .required('Secondary color is required')
        .matches(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, {
            message: 'Secondary color must be a valid hex color',
        }),

    status: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
        )
        .typeError('Status must be a number')
        .required('Status is required')
        .oneOf([0, 1], 'Status must be either 0 (inactive) or 1 (active)'),

    onboardHeader: yup
        .string()
        .required('Onboarding header is required')
        .max(500, 'Onboarding header cannot exceed 500 characters')
        .default(''),
    onboardHeaderImage: yup
        .string()
        .required('Header image is required')
        .max(2048, 'Image path is too long')
        .default(''),
    onboardSubHeader: yup
        .string()
        .required('Onboarding sub-header is required')
        .max(500, 'Onboarding sub-header cannot exceed 500 characters')
        .default(''),
    ctaButtonText: yup
        .string()
        .required('CTA button text is required')
        .max(200, 'CTA text cannot exceed 200 characters')
        .default(''),
    onboardSteps: yup
        .array()
        .of(
            yup.object({
                icon: yup
                    .string()
                    .required('Icon is required'),
                title: yup
                    .string()
                    .required('Step title is required')
                    .max(500, 'Title cannot exceed 500 characters'),
                description: yup
                    .string()
                    .max(4000, 'Description cannot exceed 4000 characters'),
            })
        )
        .min(1, 'Add at least one onboarding step')
        .required(),
})

export type ToolsFormValues = yup.InferType<typeof toolsFormSchema>

function isQuestionChipPresent(value: unknown): boolean {
    if (value instanceof File) return true
    if (typeof value === 'string') return value.trim().length > 0
    return false
}

/** Validation for Create / Edit tool question modal (CreateQuestionModal1). */
export const createQuestionFormSchema = yup.object({
    question: yup.string().required('Question is required').trim(),

    description: yup.string().required('Description is required').trim(),

    chipImage: yup
        .mixed<string | File>()
        .required('Chip image is required')
        .test('chip-required', 'Chip image is required', isQuestionChipPresent),

    isMultiSelect: yup
        .number()
        .oneOf([0, 1], 'Selection mode must be single or multi')
        .required(),

    options: yup
        .array()
        .of(
            yup.object({
                label: yup.string().required('Option label is required').trim(),
                variable_name: yup
                    .string()
                    .required('Variable name is required')
                    .trim(),
                value: yup.string().required('Value is required').trim(),
            })
        )
        .min(1, 'Add at least one option')
        .required(),
})

export type CreateQuestionFormValues = yup.InferType<typeof createQuestionFormSchema>
